import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Skeleton from '@mui/material/Skeleton';
import { AxiosError } from 'axios';
import { useRouter } from 'next/router';
import { useContext, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import {
  deleteCancelRequestJoinGroup,
  postJoinGroup,
} from '../../../apis/tms/feed.api';
import { getMineRoleOfFolder } from '../../../apis/tms/share';
import { PATH_NAME } from '../../../configs/pathName';
import { GROUP_ROLE_ACCESS } from '../../../constant/QueryKeyName';
import { ROLE_ACCESS, TRoleAccess } from '../../../constant/storage';
import useToast from '../../../hooks/useToast';
import { FolderStatusEnum } from '../../../interfaces/training.interface';
import AuthContext from '../../../state/auth/AuthContext';
import { OutlinedReload } from '../../elements/svgIcon/outlined';
import { QuickLoginModalHOC } from '../../modal/LoginModal2HOC';

interface IContent {
  initContent: string;
  undoContent: string;
}

interface IRequestJoinGroupButton extends IContent {
  groupId: string;
  groupPrivacyStatus: FolderStatusEnum;
  className?: string;
  /** this width and height use for container, skeleton, button */
  size: string;
  variant: 'outlined' | 'contained';
  containerClassname?: string;
  showIcon?: boolean;
  toastOutside?: (_content: string, _type: 'success' | 'error') => void;
}

const RequestJoinGroupButton = ({
  initContent,
  undoContent,
  groupId,
  groupPrivacyStatus,
  className = '',
  size,
  variant,
  containerClassname = '',
  showIcon,
  toastOutside,
}: IRequestJoinGroupButton) => {
  const _color = hashColor[variant];

  const [content, setContent] = useState<string>(initContent);
  const [color, setColor] = useState<string>(_color.initColor);
  const hashBgColor = {
    [`${ROLE_ACCESS.Requested}`]: _color.undoColor,
    [`${ROLE_ACCESS.Revoked}`]: _color.initColor,
    [`${ROLE_ACCESS.Owner}`]: '',
    [`${ROLE_ACCESS.Editor}`]: '',
    [`${ROLE_ACCESS.Viewer}`]: '',
  };

  const { isLoggedIn } = useContext(AuthContext);
  const [userRole, setUserRole] = useState<TRoleAccess | undefined>(
    ROLE_ACCESS.Revoked
  );

  // will not have case userRole have Owner, Editor, Viewer value
  const {
    isFetching: userRoleFetching,
    isLoading,
    refetch,
  } = useQuery(
    [GROUP_ROLE_ACCESS, groupId, 'button_only'],
    () =>
      getMineRoleOfFolder({
        folderId: groupId,
      }) as unknown as Promise<TRoleAccess>,
    {
      onSuccess(data) {
        const index = convertRole(data);
        const hashContent = {
          [`${ROLE_ACCESS.Revoked}`]: initContent,
          [`${ROLE_ACCESS.Requested}`]: undoContent,
          [`${ROLE_ACCESS.Owner}`]: '',
          [`${ROLE_ACCESS.Editor}`]: '',
          [`${ROLE_ACCESS.Viewer}`]: '',
        };
        setContent(hashContent[index]);
        setColor(hashBgColor[index]);
        setUserRole(data);
      },
      onError() {
        setContent(initContent);
        setColor(_color.initColor);
        setUserRole(ROLE_ACCESS.Revoked);
      },
      select(data) {
        return convertRole(data);
      },
      refetchOnMount: true,
      retry(_failureCount, error) {
        if ((error as AxiosError)?.response?.status === 401) {
          if (_failureCount === 2) return false;
          return true;
        }
        return false;
      },
      enabled: isLoggedIn() && !!groupId,
    }
  );

  const asyncFunction = async (isJoin: boolean) => {
    isJoin
      ? await deleteCancelRequestJoinGroup(groupId)
      : await postJoinGroup(groupId, groupPrivacyStatus);
  };

  const { mutate, isLoading: mutationLoading } = useMutation<
    void,
    Error,
    boolean
  >((_prop) => asyncFunction(_prop), {
    onSuccess() {
      refetch();
      toast(
        `Bạn đã ${
          userRole === ROLE_ACCESS.Requested ? 'hủy' : 'gửi'
        } yêu cầu tham gia nhóm`,
        'success'
      );
    },
    onError(_err) {
      if (!isLoggedIn()) {
        return;
      }
      const err = _err as AxiosError;
      if (err?.response?.status !== 400) {
        toast('Có lỗi xảy ra, vui lòng thử lại', 'error');
      }
    },
    retry: 3,
  });

  const newToast = useToast();

  const toast = toastOutside || newToast;

  const hashIcon = {
    [`${ROLE_ACCESS.Requested}`]: <OutlinedReload />,
    [`${ROLE_ACCESS.Revoked}`]: <></>,
    [`${ROLE_ACCESS.Owner}`]: <></>,
    [`${ROLE_ACCESS.Editor}`]: <></>,
    [`${ROLE_ACCESS.Viewer}`]: <></>,
  };

  const router = useRouter();

  return (
    <>
      <QuickLoginModalHOC
        classname={containerClassname}
        handleClickWhenDisabledQuickLogin={() =>
          mutate(userRole === ROLE_ACCESS.Requested ? true : false)
        }
        loginSuccessCb={() => {
          // add param to auto join fo user
          router.replace({
            pathname: PATH_NAME.MY_GROUPS_DETAIL,
            query: {
              gid: groupId,
            },
          });
          mutate(userRole === ROLE_ACCESS.Requested ? true : false);
        }}
      >
        <div
          className={`relative flex h-full w-full items-center justify-center ${size}`}
        >
          {isLoading ? (
            <SkeletonInitLoading size={size} />
          ) : (
            <>
              <Button
                startIcon={
                  !showIcon ? undefined : hashIcon[userRole as ROLE_ACCESS]
                }
                variant="contained"
                className={`!relative !normal-case !shadow-none !transition-all !ease-linear ${color} ${size} ${className}`}
              >
                {content}
              </Button>
              {(mutationLoading || userRoleFetching) && (
                <SkeletonLoading className={className} />
              )}
            </>
          )}
        </div>
      </QuickLoginModalHOC>
    </>
  );
};

function SkeletonLoading({ className }: { className: string }) {
  return (
    <div
      className={`absolute bottom-0 left-0 right-0 top-0 flex items-center justify-center bg-gray-100 opacity-50 ${className}`}
    >
      <CircularProgress size={'1.5rem'} className="z-10 !text-Primary/Main" />
    </div>
  );
}

function SkeletonInitLoading({ size }: { size: string }) {
  return (
    <Skeleton
      variant="rounded"
      width={'100%'}
      height={'100%'}
      className={`${size}`}
    />
  );
}

function convertRole(role: TRoleAccess | undefined) {
  return !role ? ROLE_ACCESS.Revoked : role;
}

interface IColor {
  initColor: string;
  undoColor: string;
}

var outlinedColor: IColor = {
  initColor:
    '!bg-transparent !text-Primary/Main hover:!bg-[#009FE433] hover:!text-Primary/Main !border !border-solid !border-[#E0E0E0]',
  undoColor:
    '!bg-transparent !text-[#7C7C7C] hover:!bg-[#B1E7FF4D] hover:!text-[#7C7C7C] !border !border-solid !border-[#E0E0E0]',
};

var containedColor: IColor = {
  initColor:
    '!bg-[#3CACDD] !text-[#FCFCFC] hover:!bg-[#009FE4] hover:!text-[#FCFCFC]',
  undoColor:
    '!bg-[#F1F1F1] !text-[#7C7C7C] hover:!bg-[#F1F1F1] hover:!text-[#7C7C7C]',
};

var hashColor = {
  contained: containedColor,
  outlined: outlinedColor,
};

export default RequestJoinGroupButton;
